import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import "./SupportUs.css";
import supportImage from '../../assets/Focus.png';
import sdgBadge from '../../assets/sdg_badge.png';
import { Heart, Users, Building, Award, Calendar, Lightbulb, Settings, GraduationCap, BookOpen } from "lucide-react";

const SupportUsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    supportType: '',
    amount: '',
    message: ''
  });

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(false);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '11c0a4ab-ec86-416a-9a30-1201b66ac3cb',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          organization: formData.organization,
          support_type: formData.supportType,
          amount: formData.amount,
          message: formData.message,
          subject: 'QuantumKE Partnership / Funding Inquiry',
          from_name: 'QuantumKE Support Form'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setFormData({ name: '', email: '', phone: '', organization: '', supportType: '', amount: '', message: '' });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const fundingAreas = [
    {
      icon: <Building className="support-option-icon" />,
      title: "Organisational Core Support",
      description: "Unrestricted funding that sustains QuantumKE's operational capacity, enabling long-term programme delivery, strategic planning, and institutional growth across Kenya and Africa."
    },
    {
      icon: <Users className="support-option-icon" />,
      title: "Quantum Hubs Programme",
      description: "Fund the establishment and scaling of QuantumKE Hubs — Education, Industry, and Research — that embed quantum capacity in local communities, universities, and partner institutions."
    },
    {
      icon: <Calendar className="support-option-icon" />,
      title: "Events & Capacity Building",
      description: "Support bootcamps, workshops, hackathons, and conferences that directly deliver skills and knowledge to quantum learners, researchers, and policymakers across the continent."
    },
    {
      icon: <Award className="support-option-icon" />,
      title: "Research & Innovation Projects",
      description: "Finance targeted quantum research initiatives, applied use-case development, and collaborative projects with academic partners including Strathmore University and the Wits Quantum Initiative."
    }
  ];

  const tocSteps = [
    {
      step: '01',
      label: 'The Problem',
      title: 'A Widening Quantum Divide',
      body: 'Africa is largely absent from the global quantum science conversation. Without deliberate investment in quantum education, research infrastructure, and policy engagement, the continent risks being left behind as quantum technology reshapes industries, economies, and security worldwide.',
      accent: 'teal',
    },
    {
      step: '02',
      label: 'Our Response',
      title: 'Targeted, Multi-Level Interventions',
      body: 'QuantumKE delivers structured programmes across three interconnected pillars: quantum education (bootcamps, eLearning, the Quantumke Academy platform), research collaboration (QResearch Hub, partnerships with university research centers), and industry & policy engagement (Quantum Anchor Hub, commercial programmes, white papers).',
      accent: 'blue',
    },
    {
      step: '03',
      label: 'Immediate Outputs',
      title: 'Measurable Deliverables',
      body: 'Trained quantum-literate individuals, certified programme completions, published research and use-case reports, convened stakeholders across academia, industry, and government, and a growing open-access eLearning platform serving learners across Africa.',
      accent: 'purple',
    },
    {
      step: '04',
      label: 'Long-Term Impact',
      title: 'A Quantum-Ready Africa',
      body: 'A self-sustaining pipeline of quantum talent, an active African quantum research ecosystem, quantum-informed policy frameworks, and equitable African participation in the global quantum economy — directly contributing to SDG 4 (Quality Education), SDG 8 (Decent Work), SDG 9 (Innovation), and Africa\'s Agenda 2063.',
      accent: 'green',
    },
  ];

  return (
    <div className="support-page">
      <Helmet>
        <title>Support QuantumKE</title>
        <meta name="description" content="Support QuantumKE's mission to advance quantum science across Africa. Partner with us, donate, or collaborate to help build Kenya's quantum technology ecosystem." />
        <link rel="canonical" href="https://quantumke.org/support-us" />
      </Helmet>

      {/* ── BANNER ── */}
      <section className="support-banner">
        <div className="support-banner-overlay" />
        <div className="support-banner-content">
          <h1 className="support-banner-title">Partner With Us</h1>
          <p className="support-banner-subtitle">
            Invest in Africa's quantum future — through catalytic funding, strategic partnerships, and shared commitment to equitable access to science and technology.
          </p>
        </div>
      </section>

      {/* ── WHY FUND ── */}
      <section className="support-why-section">
        <div className="support-container">
          <div className="support-why-content">
            <div className="support-why-text">
              <span className="support-label">The Opportunity</span>
              <h2 className="support-section-title">Why Invest in QuantumKE?</h2>
              <p className="support-description">
                QuantumKE is East Africa's leading quantum science and technology initiative, building the human capital, research capacity, and institutional infrastructure required for Africa to actively participate in — and benefit from — the coming quantum era.
              </p>
              <p className="support-description">
                We operate on a not-for-profit model: every dollar of funding is deployed directly into programme delivery, talent development, and research advancement. There are no shareholder returns — only measurable community impact.
              </p>
              <p className="support-description">
                Your investment is accountable. We provide funders with impact reporting, programme metrics, and full transparency on fund utilisation, aligned with internationally recognised M&E frameworks.
              </p>
              <div className="support-credibility-row">
                <div className="support-credibility-item">
                  <span className="support-credibility-value">500+</span>
                  <span className="support-credibility-label">Community Members</span>
                </div>
                <div className="support-credibility-item">
                  <span className="support-credibility-value">3</span>
                  <span className="support-credibility-label">Active Hubs</span>
                </div>
                <div className="support-credibility-item">
                  <span className="support-credibility-value">15+</span>
                  <span className="support-credibility-label">Global Partners</span>
                </div>
              </div>
            </div>
            <div className="support-why-image">
              <img src={supportImage} alt="QuantumKE community in action" className="support-image" />
            </div>
          </div>
        </div>
      </section>

      {/* ── THEORY OF CHANGE ── */}
      <section className="support-toc-section">
        <div className="support-container">
          <div className="support-toc-header">
            <span className="support-label">Theory of Change</span>
            <h2 className="support-section-title">How Funding Becomes Impact</h2>
            <p className="support-section-description">
              Our Theory of Change maps the causal pathway from funder investment to lasting, systemic change in Africa's quantum ecosystem.
            </p>
          </div>

          <div className="support-toc-grid">
            {tocSteps.map((item) => (
              <div key={item.step} className={`support-toc-card support-toc-${item.accent}`}>
                <div className="support-toc-step-row">
                  <span className="support-toc-step">{item.step}</span>
                  <span className="support-toc-label">{item.label}</span>
                </div>
                <h3 className="support-toc-title">{item.title}</h3>
                <p className="support-toc-body">{item.body}</p>
              </div>
            ))}
          </div>

          {/* Flow connector strip */}
          <div className="support-toc-flow">
            <div className="support-toc-flow-line" />
            {['Inputs', 'Activities', 'Outputs', 'Impact'].map((node, i) => (
              <div key={i} className="support-toc-flow-node">
                <div className="support-toc-flow-dot" />
                <span>{node}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FUNDING AREAS ── */}
      <section className="support-options-section">
        <div className="support-container">
          <div className="support-options-header">
            <span className="support-label">Funding Areas</span>
            <h2 className="support-section-title">Where Your Investment Goes</h2>
            <p className="support-section-description">
              Funders can direct support to specific programme areas or provide unrestricted core funding. All areas are underpinned by rigorous impact measurement and transparent reporting.
            </p>
          </div>
          <div className="support-options-grid">
            {fundingAreas.map((option, index) => (
              <div key={index} className="support-option-card">
                <div className="support-option-icon-container">
                  {option.icon}
                </div>
                <h3 className="support-option-title">{option.title}</h3>
                <p className="support-option-description">{option.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── IMPACT REPORT 2025-2026 ── */}
      <section className="impact-report-section">
        <div className="support-container">
          <div className="impact-report-header">
            <span className="support-label">Publications</span>
            <h2 className="support-section-title">QuantumKE Impact Report 2025-2026</h2>
            <p className="support-section-description">
              A comprehensive account of our programmes, reach, and outcomes — measuring progress across our four strategic pillars.
            </p>
          </div>

          <p className="impact-pillars-intro">
            The report documents measurable outcomes across our four strategic pillars, providing funders, partners, and the public with a transparent view of how investment translates into Africa's quantum future.
          </p>

          <div className="impact-report-layout">

            {/* Pillars panel — now on the LEFT */}
            <div className="impact-pillars-panel">
              <div className="impact-pillars-grid">
                <div className="impact-pillar-card impact-pillar-yellow">
                  <div className="impact-pillar-icon-wrap">
                    <Lightbulb className="impact-pillar-icon" />
                  </div>
                  <h4 className="impact-pillar-title">Quantum Impact</h4>
                  <p className="impact-pillar-desc">Measuring the tangible scientific and societal outcomes generated by our quantum programmes across East Africa.</p>
                </div>

                <div className="impact-pillar-card impact-pillar-orange">
                  <div className="impact-pillar-icon-wrap">
                    <Settings className="impact-pillar-icon" />
                  </div>
                  <h4 className="impact-pillar-title">Project Delivery</h4>
                  <p className="impact-pillar-desc">Tracking completion, reach, and quality of bootcamps, hackathons, research collaborations, and eLearning initiatives.</p>
                </div>

                <div className="impact-pillar-card impact-pillar-red">
                  <div className="impact-pillar-icon-wrap">
                    <BookOpen className="impact-pillar-icon" />
                  </div>
                  <h4 className="impact-pillar-title">Policy Engagement</h4>
                  <p className="impact-pillar-desc">Documenting our advocacy work, white papers, and stakeholder engagement with government bodies and international institutions.</p>
                </div>

                <div className="impact-pillar-card impact-pillar-pink">
                  <div className="impact-pillar-icon-wrap">
                    <GraduationCap className="impact-pillar-icon" />
                  </div>
                  <h4 className="impact-pillar-title">Capacity Building</h4>
                  <p className="impact-pillar-desc">Reporting on the quantum talent pipeline — learners trained, certifications awarded, and hubs established across Africa.</p>
                </div>
              </div>

            </div>

            {/* Cover mockup — now on the RIGHT */}
            <div className="impact-cover-wrap">
              <div className="impact-cover">
                {/* Decorative rings */}
                <div className="impact-cover-ring impact-cover-ring-1" />
                <div className="impact-cover-ring impact-cover-ring-2" />
                <div className="impact-cover-ring impact-cover-ring-3" />

                {/* Header bar */}
                <div className="impact-cover-topbar">
                  <span className="impact-cover-org">QuantumKE</span>
                  <span className="impact-cover-year">2025 – 2026</span>
                </div>

                {/* Title block */}
                <div className="impact-cover-title-block">
                  <h3 className="impact-cover-title">Impact<br />Report</h3>
                  <p className="impact-cover-subtitle">Advancing Quantum Science<br />Across Africa</p>
                </div>

                {/* Kenya + SDG block */}
                <div className="impact-cover-sdg-block">
                  <div className="impact-cover-sdg-left">
                    <div className="impact-cover-kenya-flag">
                      <span className="impact-flag-stripe impact-flag-black" />
                      <span className="impact-flag-stripe impact-flag-white impact-flag-thin" />
                      <span className="impact-flag-stripe impact-flag-red" />
                      <span className="impact-flag-stripe impact-flag-white impact-flag-thin" />
                      <span className="impact-flag-stripe impact-flag-green" />
                    </div>
                    <div className="impact-cover-kenya-text">
                      <span className="impact-kenya-label">Kenya</span>
                      <span className="impact-kenya-sub">East Africa</span>
                    </div>
                  </div>
                  <div className="impact-cover-sdg-right">
                    <img src={sdgBadge} alt="SDG Aligned" className="impact-sdg-badge" />
                    <span className="impact-sdg-label">SDG Aligned</span>
                  </div>
                </div>

                <div className="impact-cover-sdg-pills">
                  <span className="impact-sdg-pill impact-sdg-pill-4">SDG 4</span>
                  <span className="impact-sdg-pill impact-sdg-pill-8">SDG 8</span>
                  <span className="impact-sdg-pill impact-sdg-pill-9">SDG 9</span>
                  <span className="impact-sdg-pill impact-sdg-pill-17">SDG 17</span>
                </div>

                {/* Coming Soon footer band */}
                <div className="impact-coming-soon-footer">
                  <span className="impact-coming-soon-dot" />
                  <span>Coming Soon</span>
                </div>
              </div>

              {/* Shadow under the book */}
              <div className="impact-cover-shadow" />
            </div>

          </div>
        </div>
      </section>

      {/* ── FORM ── */}
      <section className="support-form-section">
        <div className="support-container">
          <div className="support-form-header">
            <span className="support-label">Get In Touch</span>
            <h2 className="support-section-title">Partnership & Funding Enquiry</h2>
            <p className="support-section-description">
              Complete the form below to express your interest. Our team will respond within five business days with programme details, impact reports, and next steps.
            </p>
          </div>

          <form className="support-form" onSubmit={handleSubmit}>
            <div className="support-form-grid">

              <div className="support-form-group">
                <label className="support-form-label">Full Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className="support-form-input" required />
              </div>

              <div className="support-form-group">
                <label className="support-form-label">Email Address *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className="support-form-input" required />
              </div>

              <div className="support-form-group">
                <label className="support-form-label">Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="support-form-input" />
              </div>

              <div className="support-form-group">
                <label className="support-form-label">Organisation / Foundation</label>
                <input type="text" name="organization" value={formData.organization} onChange={handleChange} className="support-form-input" />
              </div>

              <div className="support-form-group">
                <label className="support-form-label">Funding / Partnership Type *</label>
                <select name="supportType" value={formData.supportType} onChange={handleChange} className="support-form-select" required>
                  <option value="">Select type</option>
                  <option value="core">Unrestricted Core Funding</option>
                  <option value="hubs">Quantum Hubs Programme</option>
                  <option value="event">Events & Capacity Building</option>
                  <option value="research">Research & Innovation</option>
                  <option value="general">General Donation</option>
                  <option value="partnership">Strategic Partnership</option>
                </select>
              </div>

              <div className="support-form-group">
                <label className="support-form-label">Indicative Funding Amount (USD / KES)</label>
                <input type="text" name="amount" value={formData.amount} onChange={handleChange} className="support-form-input" placeholder="e.g. $10,000 or KES 1,000,000" />
              </div>

              <div className="support-form-group support-form-full">
                <label className="support-form-label">Tell us about your funding interests or proposal</label>
                <textarea name="message" value={formData.message} onChange={handleChange} className="support-form-textarea" rows="5" placeholder="Describe your organisation's focus areas, intended grant duration, any conditions, and what outcomes matter most to you." />
              </div>

            </div>

            <button type="submit" disabled={sending || success} className="support-form-submit">
              {sending ? "Sending..." :
               success ? "Enquiry submitted — we'll be in touch soon!" :
               error ? "Error — please try again" :
               <><Heart className="support-btn-icon" /> Submit Enquiry</>}
            </button>

          </form>
        </div>
      </section>

    </div>
  );
};

export default SupportUsPage;
